import { Zip, ZipPassThrough } from 'fflate';

export type BrowserFileHandle = { kind: 'file'; name: string; getFile(): Promise<File> };
export type BrowserDirectoryHandle = { kind: 'directory'; name: string; values(): AsyncIterableIterator<BrowserFileHandle|BrowserDirectoryHandle> };

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' }) => Promise<BrowserDirectoryHandle>;
  }
}

const MAX_FILES = 2_500;
const MAX_SOURCE_BYTES = 500 * 1024 * 1024;
const MAX_ZIP_BYTES = 512 * 1024 * 1024;
const ignoredDirectories = new Set(['.git', 'node_modules', '.next', '.vinext', '.wrangler', 'coverage']);
const secretNames = [/^\.env(?:\.|$)/i, /^(?:id_rsa|id_ed25519)$/i, /\.(?:pem|key|p12|pfx)$/i];

export type SelectedSiteFile = { path: string; file: File };
export type FolderPackageProgress = { completedBytes: number; totalBytes: number; fileCount: number };

function safeName(name: string) {
  if (!name || name === '.' || name === '..' || name.includes('/') || name.includes('\\') || name.includes('\0')) throw new Error(`The selected folder contains an unsafe name: ${name}`);
  return name;
}

async function collectFiles(directory: BrowserDirectoryHandle, prefix = ''): Promise<SelectedSiteFile[]> {
  const files: SelectedSiteFile[] = [];
  for await (const entry of directory.values()) {
    const name = safeName(entry.name);
    if (entry.kind === 'directory') {
      if (ignoredDirectories.has(name.toLowerCase())) continue;
      files.push(...await collectFiles(entry, `${prefix}${name}/`));
    } else {
      if (secretNames.some(pattern => pattern.test(name))) throw new Error(`Remove the potential secret file before packaging: ${prefix}${name}`);
      files.push({ path: `${prefix}${name}`, file: await entry.getFile() });
    }
  }
  return files;
}

export async function packageSelectedSiteFiles(files: SelectedSiteFile[], slug: string, onProgress?: (progress: FolderPackageProgress) => void) {
  if (!files.some(item => item.path === 'index.html')) throw new Error('Choose the finished website folder that contains index.html at its top level.');
  if (files.length > MAX_FILES) throw new Error(`The selected folder contains more than ${MAX_FILES.toLocaleString()} files.`);
  const totalBytes = files.reduce((total, item) => total + item.file.size, 0);
  if (totalBytes > MAX_SOURCE_BYTES) throw new Error('The selected folder is larger than the 500 MB direct-folder limit. Create a curated ZIP instead.');

  const chunks: ArrayBuffer[] = [];
  let resolveArchive!: (value: Blob) => void;
  let rejectArchive!: (reason: unknown) => void;
  const completed = new Promise<Blob>((resolve, reject) => { resolveArchive = resolve; rejectArchive = reject; });
  const zip = new Zip((error, data, final) => {
    if (error) { rejectArchive(error); return; }
    chunks.push(new Uint8Array(data).buffer);
    if (final) resolveArchive(new Blob(chunks, { type: 'application/zip' }));
  });

  let completedBytes = 0;
  try {
    for (const item of files.sort((a, b) => a.path.localeCompare(b.path))) {
      const stream = new ZipPassThrough(item.path);
      stream.mtime = new Date(item.file.lastModified);
      zip.add(stream);
      const reader = item.file.stream().getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        completedBytes += value.byteLength;
        stream.push(value);
        onProgress?.({ completedBytes, totalBytes, fileCount: files.length });
      }
      stream.push(new Uint8Array(0), true);
    }
    zip.end();
    const blob = await completed;
    if (blob.size > MAX_ZIP_BYTES) throw new Error('The resulting ZIP is larger than the 512 MB upload limit. Remove unused assets and try again.');
    return new File([blob], `${slug}-prospect-site.zip`, { type: 'application/zip', lastModified: Date.now() });
  } catch (error) {
    zip.terminate();
    throw error;
  }
}

export async function packageSiteDirectory(directory: BrowserDirectoryHandle, slug: string, onProgress?: (progress: FolderPackageProgress) => void) {
  return packageSelectedSiteFiles(await collectFiles(directory), slug, onProgress);
}
